import React from 'react';
import { Award, Heart, TrendingUp, Users, Leaf, Target } from 'lucide-react';

const About = () => {
  const coreValues = [
    { 
      icon: Award, 
      title: 'Quality & Trust', 
      description: 'Every grain represents our commitment to excellence and the hard work of our entire team.' 
    },
    { 
      icon: TrendingUp, 
      title: 'Growth & Innovation', 
      description: 'Continuously evolving to meet market demands while maintaining traditional values.' 
    },
    { 
      icon: Leaf, 
      title: 'Sustainable Future', 
      description: 'Committed to eco-friendly practices and reversing the loss of biodiversity.' 
    },
    { 
      icon: Heart, 
      title: 'Honesty & Integrity', 
      description: 'Building lasting relationships through transparency and ethical business practices.' 
    },
    { 
      icon: Users, 
      title: 'Responsibility & Ownership', 
      description: 'Every team member takes pride in delivering the finest Basmati rice.' 
    },
    { 
      icon: Target, 
      title: 'Leadership Collaboration', 
      description: 'Working together with farmers, partners, and customers as one family.' 
    },
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section 
        className="relative h-[60vh] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://images.unsplash.com/photo-1611501355758-0d8b8208e1ab')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            About <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300">Dreampack</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
            A Strong Belief, A Bold Dream
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                India's Premier
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-500">
                  Export and Rice Trading House
                </span>
              </h2>
              <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                Dreampack Solutions Private Limited represents a proud legacy of 3rd generation entrepreneurs, pioneering as India's premier export and rice trading house specializing in premium Basmati rice.
              </p>
              <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                Under the visionary leadership of our Co-Founders Hena Bhatia and Vaibhav Bhatia, we have built a reputation for excellence, processing 300 metric tons of rice daily and selling over 5000 metric tons annually across India and international markets.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our flagship brand, Amyrah Basmati Rice, embodies our commitment to quality, sustainability, and customer satisfaction, making us a trusted partner for discerning customers worldwide, especially in GCC countries.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/17052142/pexels-photo-17052142.jpeg" 
                alt="Premium Basmati Rice" 
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex p-4 bg-gradient-to-r from-amber-600 to-yellow-500 rounded-full mb-6">
              <Target size={40} className="text-white" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Vision</h2>
            <p className="text-xl text-gray-700 leading-relaxed italic">
              "To grow within the company keeping a balance between customer satisfaction and employee happiness quotient. Thus providing the optimum product in the form of finest basmati rice. Every grain represents the hard work of the company."
            </p>
            <div className="mt-8 flex justify-center space-x-12">
              <div className="text-center">
                <p className="text-3xl font-bold text-amber-600">5000+</p>
                <p className="text-gray-600 mt-2">Metric Tons Sold</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-amber-600">300</p>
                <p className="text-gray-600 mt-2">MT Processing/Day</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-amber-600">PAN India</p>
                <p className="text-gray-600 mt-2">Market Presence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Core <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-500">Values</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do at Dreampack
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreValues.map((value, index) => {
              const Icon = value.icon;
              return (
                <div 
                  key={index} 
                  className="bg-gradient-to-br from-white to-amber-50 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-amber-100"
                >
                  <div className="inline-flex p-3 bg-gradient-to-r from-amber-600 to-yellow-500 rounded-full mb-4">
                    <Icon size={28} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Farm to Fork */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/6086557/pexels-photo-6086557.jpeg" 
                alt="Farm to Fork" 
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Farm to Fork
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-500">
                  Sustainable Strategy
                </span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our Farm to Fork strategy accelerates our transition to a sustainable food system that benefits everyone - from farmers to consumers.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Leaf className="text-green-600 mr-3 flex-shrink-0 mt-1" size={24} />
                  <span className="text-gray-700">Neutral or positive environmental impact</span>
                </li>
                <li className="flex items-start">
                  <Leaf className="text-green-600 mr-3 flex-shrink-0 mt-1" size={24} />
                  <span className="text-gray-700">Help mitigate climate change and adapt to its impacts</span>
                </li>
                <li className="flex items-start">
                  <Leaf className="text-green-600 mr-3 flex-shrink-0 mt-1" size={24} />
                  <span className="text-gray-700">Reverse the loss of biodiversity</span>
                </li>
                <li className="flex items-start">
                  <Leaf className="text-green-600 mr-3 flex-shrink-0 mt-1" size={24} />
                  <span className="text-gray-700">Ensure food security, nutrition, and public health</span>
                </li>
                <li className="flex items-start">
                  <Leaf className="text-green-600 mr-3 flex-shrink-0 mt-1" size={24} />
                  <span className="text-gray-700">Generate fairer economic returns and promote fair trade</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Certified <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-500">Excellence</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our commitment to quality is backed by international certifications
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center justify-items-center">
            {[38, 39, 40, 41].map((num) => (
              <div key={num} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300">
                <img 
                  src={`/images/slide_12_image_${num}.png`} 
                  alt={`Certification ${num}`}
                  className="w-full h-32 object-contain"
                />
              </div>
            ))}
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300">
              <img 
                src="/images/sfda-logo-new.jpg" 
                alt="Saudi Food and Drug Authority"
                className="w-full h-32 object-contain"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;